"use client";

import { Button } from "@/app/_components/ui/button";
import { attemptChunkLoadReload } from "@/lib/errors/chunk-load-reload-guard";
import { isTransientRenderError } from "@/lib/errors/is-transient-render-error";
import { getSupportContactForUserType } from "@/lib/errors/support-contact";
import getLogger from "@/lib/logger";
import { CircleAlertIcon, RefreshCwIcon } from "lucide-react";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";

const logger = getLogger("route-error-boundary");

/**
 * In-layout error state for a route group's `error.tsx`. The surrounding
 * layout (nav, sidebar, portal chrome) stays mounted, so a throw in one
 * subtree no longer takes the whole document down to `global-error`.
 *
 * Transient errors (a stale chunk after a deploy, a dropped fetch) reload the
 * page instead of showing an error the user cannot act on, bounded by the
 * shared per-build guard.
 */
export function RouteErrorBoundary({
  error,
  reset,
  boundary,
}: {
  error: Error & { digest?: string };
  reset: () => void;
  boundary: string;
}) {
  const { data: session, status } = useSession();
  const isTransient = isTransientRenderError(error);
  // Shares the per-build attempt counter with `global-error`, so a tab that
  // already burned its reload on a still-broken build gets the error state
  // here instead of looping.
  const [isReloading] = useState(() => isTransient && attemptChunkLoadReload());

  useEffect(() => {
    const context = {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        digest: error.digest,
      },
      boundary,
      userType: session?.user?.userType,
      userId: session?.user?.id,
      sessionStatus: status,
    };

    if (isTransient) {
      logger.warn(
        context,
        `Transient route error in ${boundary}: ${error.message}`,
      );
      return;
    }

    logger.error(context, `Route error in ${boundary}: ${error.message}`);
  }, [boundary, error, isTransient, session, status]);

  // Render nothing while a reload is pending. Once the guard has spent this
  // build's attempt, fall through to the error state rather than leaving the
  // user on a blank panel.
  if (isReloading) {
    return null;
  }

  return (
    <div
      role="alert"
      className="flex min-h-[320px] w-full flex-1 flex-col items-center justify-center px-6 py-16"
    >
      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        <CircleAlertIcon
          aria-hidden
          className="size-6 text-muted-foreground"
          strokeWidth={1.5}
        />
        <h2 className="font-martina text-2xl leading-tight text-foreground">
          This part of the page failed to load
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Try again, and if it keeps happening,{" "}
          <SupportContactMessage userType={session?.user?.userType} />.
        </p>
        <Button className="mt-2" onClick={reset} variant="default">
          <RefreshCwIcon className="mr-2 size-4" aria-hidden />
          Try again
        </Button>
      </div>
    </div>
  );
}

function SupportContactMessage({ userType }: { userType?: string }) {
  const contact = getSupportContactForUserType(userType);

  if (contact.kind === "school-success-partner") {
    return <>reach out to your school success partner</>;
  }

  return (
    <>
      reach out to{" "}
      <a
        className="text-primary underline underline-offset-2"
        href={`mailto:${contact.email}`}
      >
        {contact.email}
      </a>
    </>
  );
}
